import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {
  maybeGetValueFromConfig,
  setValueInConfig,
} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import AddIcon from 'ui-library/icons/AddIcon';
import DeleteIcon from 'ui-library/icons/DeleteIcon';
import {immutableRemoveAt, immutableReplace} from 'util/ImmutableListUtil';
import {isJSONArray, assertJSONObject, JSONObject, JSONValue} from 'util/Types';

import jsonSpecToInitialValue from '../initial-values/jsonSpecToInitialValue';
import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  ArrayExpandableItemControls,
  ConfigShapePath,
  EXPANDABLE_DETAILS_KEY,
  FieldUISchemaWithStandardComponent,
  isShapeUISchema,
  ShapeUISchema,
} from '../StepConfigEditorTypes';
import StepConfigEditorRouter from './StepConfigEditorRouter';

// Stable client-side ids for array items. Survive a component remount, but reset on full page load.
const itemIdsGlobalState = makeUseGlobalClientState<Array<string>>([]);
let idCounter = 0;
const mintId = (): string => `arritem-${idCounter++}`;

export interface ArrayEditorProps {
  config: JSONObject;
  configShape: GetPipelineConfigSpecType<'array'>;
  dataTestAttrs?: Pick<DataTestAttrs, 'key' | 'index'>;
  onChangeConfig: (config: JSONObject, path: ConfigShapePath, newValue: JSONValue) => void;
  elementUISchema?: FieldUISchemaWithStandardComponent | ShapeUISchema;
  disabled?: boolean;
  configShapePath: ConfigShapePath;
  disableAddRemove?: boolean;
  /** Accessible label for the add-element control (e.g. "Add parameter"). */
  addButtonAltText?: string;
  hideDefaultAddButton?: boolean;
}

export default function ArrayEditor({
  config,
  configShape,
  dataTestAttrs = {},
  onChangeConfig,
  disabled,
  configShapePath,
  elementUISchema,
  disableAddRemove,
  addButtonAltText,
  hideDefaultAddButton,
}: ArrayEditorProps): JSX.Element {
  const {clientStateId} = useStepConfigEditorContext();
  const arrayValue = maybeGetValueFromConfig(config, configShapePath) ?? [];

  assert(isJSONArray(arrayValue), 'arrayValue is not an array');

  const arrayStateKey = `arritemids-${configShapePath.join('.')}-${clientStateId}`;
  const [storedIds, setStoredIds] = itemIdsGlobalState.useGlobalClientState(arrayStateKey);

  // Reconcile ids to the array during render without dispatching to Redux mid-render.
  const ids = React.useMemo(() => {
    const next = (storedIds ?? []).slice(0, arrayValue.length);
    while (next.length < arrayValue.length) {
      next.push(mintId());
    }
    return next;
  }, [storedIds, arrayValue.length]);

  const isDeleteDisabled = disableAddRemove || arrayValue.length <= (configShape.minItems ?? 0);
  const isAddDisabled =
    disableAddRemove || (configShape.maxItems != null && arrayValue.length >= configShape.maxItems);

  const handleElementDelete = (index: number): void => {
    setStoredIds(immutableRemoveAt(ids, index));
    const newArray = immutableRemoveAt(arrayValue, index);
    onChangeConfig(config, configShapePath, newArray);
  };

  const handleElementAdd = (): void => {
    setStoredIds([...ids, mintId()]);
    const newElement = jsonSpecToInitialValue(configShape.elementSpec, elementUISchema);
    const newArray = [...arrayValue, newElement];
    onChangeConfig(config, configShapePath, newArray);
  };

  const renderDeleteControl = (index: number, dataTestAttrs?: DataTestAttrs): JSX.Element => (
    <Tooltip
      isDisabled={!isDeleteDisabled}
      text={`Must have at least ${configShape.minItems ?? 0} ${pluralize(
        configShape.minItems ?? 0,
        'item',
        'items'
      )}`}
    >
      <DeleteIcon
        icon="trash-can"
        altText="Delete item"
        disabled={isDeleteDisabled}
        onClick={() => {
          handleElementDelete(index);
        }}
        dataTestAttrs={dataTestAttrs}
      />
    </Tooltip>
  );

  const isShapeElement = elementUISchema != null && isShapeUISchema(elementUISchema);

  const isExpandableItem = isShapeElement && elementUISchema?.[EXPANDABLE_DETAILS_KEY] != null;

  const props = {
    config,
    configShape,
    dataTestAttrs,
    onChangeConfig,
    disabled,
    configShapePath,
    arrayValue,
  };

  return (
    <>
      {arrayValue.map((element: JSONValue, index: number): JSX.Element => {
        const itemId = ids[index];
        const arrayItemControls: ArrayExpandableItemControls = {
          deleteComponent: renderDeleteControl(index, {
            component: 'ArrayEditorV2',
            element: 'inHeaderDeleteButton',
          }),
          stateKeySuffix: itemId,
        };
        return (
          <div
            key={itemId}
            className={css(styles.arrayItemContainer)}
            {...getTestProps({component: 'ArrayEditorV2', element: 'item', index: index.toString()})}
          >
            <div className={css(styles.arrayItemContainerInner)}>
              {isShapeElement ? (
                <ShapeElement
                  element={element}
                  index={index}
                  elementUISchema={elementUISchema}
                  arrayItemControls={isExpandableItem ? arrayItemControls : undefined}
                  {...props}
                />
              ) : (
                <ScalarElement element={element} index={index} elementUISchema={elementUISchema} {...props} />
              )}
            </div>
            {!disabled && !disableAddRemove && !isExpandableItem && (
              <div className={css(styles.arrayItemContainerDeleteButton)}>{renderDeleteControl(index)}</div>
            )}
          </div>
        );
      })}
      {!disabled && !disableAddRemove && !hideDefaultAddButton && (
        <div>
          <Tooltip
            isDisabled={!isAddDisabled}
            text={configShape.maxItems != null ? `Cannot exceed ${configShape.maxItems} items` : ''}
          >
            <AddIcon
              disabled={isAddDisabled}
              onClick={handleElementAdd}
              altText={addButtonAltText ?? 'Add item'}
              dataTestAttrs={{
                component: 'AddArrayElementIcon',
                element: 'root',
                ...dataTestAttrs,
              }}
            />
          </Tooltip>
        </div>
      )}
    </>
  );
}

interface BaseElementProps {
  element: JSONValue;
  index: number;
  config: JSONObject;
  configShape: GetPipelineConfigSpecType<'array'>;
  dataTestAttrs: Pick<DataTestAttrs, 'key' | 'index'>;
  onChangeConfig: (config: JSONObject, path: ConfigShapePath, newValue: JSONValue) => void;
  disabled?: boolean;
  configShapePath: ConfigShapePath;
  arrayValue: Array<JSONValue>;
}

interface ShapeElementProps extends BaseElementProps {
  elementUISchema: ShapeUISchema;
  arrayItemControls?: ArrayExpandableItemControls;
}

interface ScalarElementProps extends BaseElementProps {
  elementUISchema?: FieldUISchemaWithStandardComponent;
}

/*
 * Render a shape/object element (e.g., {name: 'foo', dataType: 'text'})
 * The Router will auto-generate a minimal ShapeUISchema for it.
 */
function ShapeElement({
  element,
  index,
  config,
  configShape,
  dataTestAttrs,
  onChangeConfig,
  elementUISchema,
  disabled,
  configShapePath,
  arrayValue,
  arrayItemControls,
}: ShapeElementProps): JSX.Element {
  assertJSONObject(element);
  return (
    <StepConfigEditorRouter
      config={element}
      configShape={configShape.elementSpec}
      dataTestAttrs={{...dataTestAttrs, index: index.toString()}}
      onChangeConfig={(elementConfig, path, newValue) => {
        // Base the update on `elementConfig`, which already reflects any ON_CHANGE_CONFIG_MIDDLEWARE_KEY
        // transform, so middleware-applied changes are kept.
        assertJSONObject(elementConfig);
        const updatedElement = setValueInConfig(elementConfig, path, newValue);
        onChangeConfig(config, configShapePath, immutableReplace(arrayValue, index, updatedElement));
      }}
      uiSchema={elementUISchema}
      disabled={disabled}
      arrayItemControls={arrayItemControls}
    />
  );
}

/*
 * Render a scalar element (e.g., 'string', 42, true) by wrapping in a temporary shape.
 */
function ScalarElement({
  element,
  index,
  config,
  configShape,
  dataTestAttrs,
  onChangeConfig,
  elementUISchema,
  disabled,
  configShapePath,
  arrayValue,
}: ScalarElementProps): JSX.Element {
  return (
    <StepConfigEditorRouter
      config={{element}}
      configShape={{kind: 'shape', fields: {element: configShape.elementSpec}, required: true}}
      dataTestAttrs={{...dataTestAttrs, index: index.toString()}}
      onChangeConfig={(_, __, newValue) => {
        // For scalar elements, newValue is the updated element directly
        onChangeConfig(config, configShapePath, immutableReplace(arrayValue, index, newValue));
      }}
      uiSchema={
        elementUISchema
          ? {
              ...elementUISchema,
              configShapePath: ['element'],
            }
          : {
              configShapePath: ['element'],
            }
      }
      disabled={disabled}
    />
  );
}

const styles = StyleSheet.create({
  arrayItemContainer: {display: 'flex', alignItems: 'flex-start', gap: '8px'},
  arrayItemContainerInner: {flex: 1},
  arrayItemContainerDeleteButton: {marginTop: '8px'},
});
